/**
 * 调用桥
 * 基于 KNB.use 进行封装
 * 支持打点、耗时
 * @author 2020-07-13 by YuanXuJia
 */
// tslint:disable:trailing-comma
import { getProp } from '@mx/util/get-prop';
import { NeoError } from '../util/neo-error';
import { NeoNamespace } from '../common-enum';
import { debug, DebugLevel } from '../util/debug';
import { stat, BridgeSendType, StatErrorLevel } from '../stat';
import { BridgeErrorType, ExecuteBridgeResult, BridgeName } from './constants';

/**
 * 桥调用参数
 */
export interface ExecuteBridgeParams {
  // 桥名称
  bridgeName: string;
  // 桥需要的必要数据
  bridgeData: BridgeData;
  // 超时时间
  timeout?: number;
  // 统计参数
  statParams?: {
    retryTimes: number;
    retryDelay: number;
  };
  // debug 标识
  debug?: boolean;
}
/**
 * 业务桥需要使用的数据
 */
export interface BridgeData {
  [key: string]: any;
  action?: string;
}

interface BridgeStatParams {
  [key: string]: any;
  bridge_name: string;
  action: string;
  keys?: string[];
  data?: PureObject;
  timeout: number;
  retry_times?: number;
  retry_delay?: number;
}

/**
 * 封装 KNB.use 的调用
 * 调用有3中状态：
 * 成功、失败、超时失败
 * @param params 桥参数
 */
function execute(
  params: ExecuteBridgeParams,
  opts: PlainObject<any> = {}
): Promise<any> {
  return new Promise((resolve, reject) => {
    KNB.use(params.bridgeName, {
      ...params.bridgeData,
      success: (data: ExecuteBridgeResult<any>) => {
        opts.lxParams.lab.bridge_time = Date.now() - opts.startTime;
        opts.lxParams.lab.status = getProp(data, ['response', 'status'], '');
        stat.sendBridgeInfo(BridgeSendType.success, opts.lxParams);
        resolve(data);
      },
      fail: (info: ExecuteBridgeResult<any>) => reject(info),
    });
    // 启动超时逻辑
    if (params.timeout) {
      opts.timeoutId = window.setTimeout(() => {
        reject(
          new NeoError(
            NeoNamespace.BridgeExecute,
            `bridge '${params.bridgeName}' execute timeout in ${params.timeout}ms`,
            BridgeErrorType.Timeout
          )
        );
      }, params.timeout);
    }
  });
}

/**
 * 获取桥调用埋点参数
 * @param key
 * @param data
 * @param params
 * @returns 桥调用埋点参数
 */
function getBridgeStatParams(
  bridgeName: string,
  data: any,
  params: ExecuteBridgeParams
): BridgeStatParams {
  const { action, success, fail, ...restData } = data;
  const statParams: BridgeStatParams = {
    bridge_name: bridgeName,
    action: action || '',
    timeout: (params && params.timeout) || 0,
    ...restData,
  };

  // 重试统计字段
  if (params.statParams) {
    if ('retryTimes' in params.statParams) {
      statParams.retry_times = params.statParams.retryTimes;
    }
    if ('retryDelay' in params.statParams) {
      statParams.retry_delay = params.statParams.retryDelay;
    }
  }

  // pay.sharkRequest 桥调用不上报请求参数（可能存在超长参数，导致上报失败）
  if (bridgeName === 'pay.sharkRequest' && statParams.data) {
    const { params: reqParams, ...reqRestData } = getProp(
      statParams,
      ['data'],
      {}
    );
    statParams.data = reqRestData;
  }

  return statParams;
}

// 桥调用前数据埋点
function reportBridgeStart(params: ExecuteBridgeParams): {
  lab: PlainObject<any>;
} {
  // 统计参数
  const lxParams = {
    lab: getBridgeStatParams(params.bridgeName, params.bridgeData, {
      ...params,
    }),
  };
  stat.sendBridgeInfo(BridgeSendType.start, lxParams);
  return lxParams;
}

/**
 * 清除桥超时
 * @param timeoutId 时间id
 */
function clearBridgeTimeout(timeoutId: number): void {
  if (timeoutId) {
    window.clearTimeout(timeoutId);
  }
}

/**
 * 桥调用
 * @param params
 * @returns
 */
export function executeBridge<T>(
  params: ExecuteBridgeParams
): Promise<ExecuteBridgeResult<T>> {
  debug(
    NeoNamespace.BridgeExecute,
    DebugLevel.Info,
    'executeBridge 入参：',
    params
  );
  const startTime = Date.now();
  const lxParams = reportBridgeStart(params);
  const opts = { timeoutId: 0, startTime, lxParams };

  return execute(params, opts)
    .finally(() => {
      lxParams.lab.bridge_time = Date.now() - startTime;
      clearBridgeTimeout(opts.timeoutId);
    })
    .then((data: ExecuteBridgeResult<T> | NeoError) => {
      debug(
        NeoNamespace.BridgeExecute,
        DebugLevel.Info,
        `bridge name: ${params.bridgeName} | 出参:`,
        data
      );

      const hasData =
        getProp(data, ['response', 'status'], '') ||
        getProp(data, ['response', 'data'], '');
      if (hasData) {
        lxParams.lab.status = getProp(data, ['response', 'status'], '');
        stat.sendBridgeInfo(BridgeSendType.active, lxParams);
      }
      return data as ExecuteBridgeResult<T>;
    })
    .catch((err: NeoError | ExecuteBridgeResult<any>) => {
      lxParams.lab = {
        ...lxParams.lab,
        status: 'fail',
        error_type: (err as NeoError)?.type || '',
        bridge_error: err,
      };
      stat.sendBridgeInfo(BridgeSendType.inactive, lxParams);
      return Promise.reject(err);
    });
}

/**
 * 处理 桥相关异常
 * @param result 桥的返回数据
 * @param bridgeName 当前桥的名称
 */
export function normalizeBridgeError<T>(
  result: ExecuteBridgeResult<T>,
  bridgeName: BridgeName
): void {
  const { status, data, error } = result.response;
  if (!result || !result.response) {
    throw new NeoError(
      NeoNamespace.Bridge,
      'unexpected data structure',
      bridgeName
    );
  }
  // 如果没有 status 字段，说明桥返回数据不符合预期
  if (!status) {
    throw new NeoError(
      NeoNamespace.Bridge,
      "expected property 'status' is 'success' or 'fail'",
      bridgeName
    );
  }
  // Native 内部错误时返回失败状态
  if (status === 'fail') {
    throw new NeoError(
      NeoNamespace.Bridge,
      `${error?.error_code} - ${error?.error_message} `,
      bridgeName
    );
  }
  // Native 内部错误，桥调用成功
  if (status === 'success' && !data) {
    throw new NeoError(
      NeoNamespace.Bridge,
      "property 'data' is empty",
      bridgeName
    );
  }
  // TODO 定位 dispatcher 数据为空临时添加
  if (!data) {
    stat.sendToRaptor(
      '非预期桥调用返回数据结构',
      { bridge_name: bridgeName, ...result },
      StatErrorLevel.Error
    );
  }
}
